'use client'

import { useRouter } from 'next/navigation'
import { useState, useCallback } from 'react'
import { signIn } from 'next-auth/react'
import Link from 'next/link'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@awesome.me/kit-94e593bf23/icons/sharp/light'
import { faSpinnerThird } from '@awesome.me/kit-94e593bf23/icons/classic/light'

import Panel from '@/app/_components/Panel'
import FormError from '@/app/_components/FormError'
import RecaptchaDisclaimer from '@/app/_components/RecaptchaDisclaimer'
import { useReCaptcha } from 'next-recaptcha-v3'
import { trackLogin } from '@/app/_lib/analytics/ga4'

const signInSchema = z.object({
    email: z.string().min(1, 'Email is required').email('Please enter a valid email'),
    password: z.string().min(1, 'Password is required'),
})

const SignInForm = ({ className }) => {

    const router = useRouter()
    const { executeRecaptcha } = useReCaptcha()

    const [serverError, setServerError] = useState(null)
    const [fadeOutMessage, setFadeOutMessage] = useState(false)

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting }
    } = useForm({
        resolver: zodResolver(signInSchema),
        defaultValues: {
            email: '',
            password: '',
        }
    })

    const onSubmit = async (data) => {
        // Fade out existing error message if present
        if (serverError) {
            setFadeOutMessage(true)
            await new Promise(resolve => setTimeout(resolve, 300))
        }

        // Get reCAPTCHA token
        const recaptchaToken = await executeRecaptcha('login')
        if (!recaptchaToken) {
            setServerError('Security verification failed. Please refresh the page and try again.')
            setFadeOutMessage(false)
            return
        }

        // Use NextAuth signIn with credentials provider
        // Wrap in timeout to prevent infinite hang
        try {
            const signInPromise = signIn('credentials', {
                email: data.email,
                password: data.password,
                recaptchaToken: recaptchaToken,
                redirect: false,
            })

            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('SIGNIN_TIMEOUT')), 30000)
            )

            const result = await Promise.race([signInPromise, timeoutPromise])

            console.log('signIn result:', result)

            if (result?.ok) {
                trackLogin()
                router.replace('/account')
            } else {
                // Sign in failed - generic message for security
                console.log('signIn failed:', result?.error)
                setServerError('Invalid email or password. Please try again.')
                setFadeOutMessage(false)
            }
        } catch (error) {
            console.error('signIn exception:', error)
            if (error.message === 'SIGNIN_TIMEOUT') {
                setServerError('Sign in is taking too long. Please refresh and try again.')
            } else {
                setServerError('An error occurred. Please try again.')
            }
            setFadeOutMessage(false)
        }
    }

    const [passwordVisible, setPasswordVisible] = useState(false)

    const togglePasswordVisibility = useCallback(() => {
        setPasswordVisible(prev => !prev)
    }, [])

    const [isGoogleLoading, setIsGoogleLoading] = useState(false)
    const [isAppleLoading, setIsAppleLoading] = useState(false)

    const handleGoogleSignIn = async (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsGoogleLoading(true)
        try {
            await signIn('google', { callbackUrl: '/account' })
        } catch (error) {
            console.error('Google sign-in error:', error)
            setIsGoogleLoading(false)
        }
    }

    const handleAppleSignIn = async (e) => {
        e.preventDefault()
        e.stopPropagation()
        // Apple OAuth not yet configured
        console.log('Apple sign-in not yet implemented')
    }
    
    return (
        <>
            <Panel
                label="Sign In"
                className={`w-full ${className || ''}`}
                headerAccessory={
                    <div className="md:hidden ml-auto text-2xs font-normal">
                        Don&apos;t have an account? <Link href="/account/new" className="underline underline-offset-2">Create Account</Link>
                    </div>
                }
            >
                <form onSubmit={handleSubmit(onSubmit)}>
                    {serverError && (
                        <div
                            role="alert"
                            aria-live="assertive"
                            className={`text-red font-medium p-3 border-l-4 border-red mb-4 transition-opacity duration-300 ${fadeOutMessage ? 'opacity-0' : 'opacity-100'}`}
                        >
                            {serverError}
                        </div>
                    )}
                    <div className="form-group mb-4">
                        <input
                            id="email"
                            type="email"
                            autoComplete="username"
                            className={`form-input peer ${errors.email ? 'border-red' : ''}`}
                            placeholder=" "
                            aria-invalid={!!errors.email}
                            aria-describedby={errors.email ? "email-error" : undefined}
                            {...register('email')}
                        />
                        <label htmlFor="email" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">Email <span className="text-primary">*</span></label>
                        {errors.email && (
                            <FormError id="email-error">{errors.email.message}</FormError>
                        )}
                    </div>
                    <div className="form-group mb-4">
                        <input
                            type={passwordVisible ? 'text' : 'password'}
                            id="password"
                            autoComplete="current-password"
                            className={`form-input peer pr-8 ${errors.password ? 'border-red' : ''}`}
                            placeholder=" "
                            aria-invalid={!!errors.password}
                            aria-describedby={errors.password ? "password-error" : undefined}
                            {...register('password')}
                        />
                        <label htmlFor="password" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">Password <span className="text-primary">*</span></label>
                        <button
                            type="button"
                            onClick={togglePasswordVisibility}
                            aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                            aria-pressed={passwordVisible}
                            title={passwordVisible ? 'Hide password' : 'Show password'}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            <FontAwesomeIcon icon={passwordVisible ? faEye : faEyeSlash} className="text-xs" aria-hidden="true" />
                        </button>
                        {errors.password && (
                            <FormError id="password-error">{errors.password.message}</FormError>
                        )}
                    </div>
                    <div className="flex items-center">
                        <button type="submit" disabled={isSubmitting} className={`w-1/2 mr-5 btn btn-primary flex items-center justify-center ${isSubmitting ? 'opacity-75' : ''}`}>
                            {isSubmitting ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin mr-2" />
                                    Signing in...
                                </>
                            ) : (
                                'Sign In'
                            )}
                        </button>
                        <Link
                            href="/account/reset-password"
                            className="text-sm text-light underline underline-offset-2 hover:text-primary transition-colors"
                        >
                            Reset Password
                        </Link>
                    </div>
                </form>

                <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="hidden md:inline px-2 bg-white text-gray-500">Or continue with</span>
                    </div>
                </div>

                <button
                    onClick={handleGoogleSignIn}
                    disabled={isGoogleLoading}
                    type="button"
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-10 rounded text-sm font-medium text-ink bg-white hover:border-primary hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {isGoogleLoading ? (
                        <>
                            <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin" />
                            Connecting to Google...
                        </>
                    ) : (
                        <>
                            <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                            </svg>
                            Sign in with Google
                        </>
                    )}
                </button>

                <div className="border-t border-gray-300 my-6 md:hidden"></div>

                <Link href="/checkout/guest" className="block btn btn-secondary md:hidden">
                    Guest Checkout
                </Link>

                {/* Apple Sign In - Hidden until configured */}
                {false && (
                    <button
                        onClick={handleAppleSignIn}
                        disabled={isAppleLoading}
                        type="button"
                        className="w-full flex items-center justify-center gap-3 px-4 py-3 mt-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                    >
                        {isAppleLoading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin" />
                                Connecting to Apple...
                            </>
                        ) : (
                            <>
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                                </svg>
                                Sign in with Apple
                            </>
                        )}
                    </button>
                )}
                <div className="mt-4">
                    <RecaptchaDisclaimer />
                </div>
            </Panel>
        </>
    )
}

export default SignInForm
